import React, { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'universal-cookie';
import queryString from 'query-string';
import { useFlags, useFlagsStatus, useUnleashContext } from '@unleash/proxy-client-react';
import { ExperimentContext, ExperimentProvider } from './context';
import { generateExperimentSetupMapping } from './utils';
import { getEnabledExperiments } from '../featureFlags';
import { isRobotE2E } from '../utils/e2eBotUtils';
import { setCrossDomainCookie } from '../utils/cookieUtils';
import { EXPERIMENT__DIFFERENT_PLAN_TRIALS } from './constants';

const EXPERIMENT_COOKIE = 'codeSparkExperimentID';
const FEATURE_ID_COOKIE = 'featureId';
export const EXPERIMENT_OVERRIDE_COOKIE = 'variantOverride';

function useExperimentContextSetup() {
  const updateContext = useUnleashContext();

  const cookies = new Cookies();
  const variantOverrideCookie = cookies.get(EXPERIMENT_OVERRIDE_COOKIE);
  const existingExperimentId = cookies.get(EXPERIMENT_COOKIE);
  const featureIdCookie = cookies.get(FEATURE_ID_COOKIE);
  const experimentId = !!existingExperimentId ? existingExperimentId : uuidv4();

  useEffect(() => {
    const parsedQuery = queryString.parse(window.location.search);
    const variantOverride = parsedQuery.variantOverride || variantOverrideCookie;
    const featureIds = parsedQuery.featureId || featureIdCookie;

    // setup persistant experiment id for unleash stickiness across foos and fooaccounts
    // since unleash uses localstorage and that does not persist between subdomains
    updateContext({
      codeSparkExperimentID: experimentId,
      platformType: 'web',
      isRobotE2E: isRobotE2E(),
      variantOverride,
      featureIds
    });

    setCrossDomainCookie(EXPERIMENT_COOKIE, experimentId);
    if (variantOverride) {
      setCrossDomainCookie(EXPERIMENT_OVERRIDE_COOKIE, variantOverride);
    }
    if (featureIds) {
      setCrossDomainCookie(FEATURE_ID_COOKIE, featureIds);
    }
  }, [experimentId, updateContext, setCrossDomainCookie, variantOverrideCookie]);
}

function ExperimentsWrapper({ children }) {
  useExperimentContextSetup();
  const flags = useFlags();
  const { flagsReady, flagsError } = useFlagsStatus();

  return (
    <ExperimentProvider>
      <ExperimentSetup flags={flags} flagsReady={flagsReady} flagsError={flagsError}>
        {children}
      </ExperimentSetup>
    </ExperimentProvider>
  );
}

// https://github.com/nfl/react-helmet/issues/437
// Do not use useEffect where the child is a component using <Helmet> due to bug related to
// older version of react-helmet being used. useEffect can be used if the package is updated
class ExperimentSetup extends React.PureComponent {
  static contextType = ExperimentContext;

  componentDidUpdate(prevProps) {
    const { flagsReady, flags } = this.props;

    if (flagsReady && !prevProps.flagsReady) {
      const enabledExperiments = getEnabledExperiments(flags);
      enabledExperiments.forEach(enabledExperiment => {
        const variant = enabledExperiment.variant.name;

        const experimentVariantSetupFn = this.getExperimentSetupFns(
          enabledExperiment.name,
          variant
        );

        if (experimentVariantSetupFn) {
          experimentVariantSetupFn();
        }
      });
    }
  }

  getExperimentSetupFns(...args) {
    const { setExperimentStatus } = this.context;

    return generateExperimentSetupMapping({
      C136_Modernized_Homepage_With_OnDemand_Classes: {
        control: () => {
          setExperimentStatus('passPlanTagToAccounts', true);
          setExperimentStatus('homepageRedesign', false);
        },
        variant1: () => {
          setExperimentStatus('passPlanTagToAccounts', true);
          setExperimentStatus('homepageRedesign', true);
        },
      },
      C141_LP_Only_Premium_Plans: {
        control: () => {
          setExperimentStatus('premiumOndemandClasses', false);
        },
        variant1: () => {
          setExperimentStatus('premiumOndemandClasses', true);
        },
      },
      C142_Annual_VS_Low_Term_Non_Premium: {
        control: () => {
          setExperimentStatus('premiumVsStandardDesign', false);
        },
        variant1: () => {
          setExperimentStatus('premiumVsStandardDesign', true);
        },
      },
      C143_2_Year: {
        control: () => {
          setExperimentStatus('premiumVsStandardDesign', false);
        },
        variant1: () => {
          setExperimentStatus('premiumVsStandardDesign', true);
        },
      },
      C144_Trial_Incentive: {
        control: () => {
          setExperimentStatus('premiumOndemandClassesTrials', true);
          setExperimentStatus(EXPERIMENT__DIFFERENT_PLAN_TRIALS, false);
        },
        variant1: () => {
          setExperimentStatus('premiumOndemandClassesTrials', true);
          setExperimentStatus(EXPERIMENT__DIFFERENT_PLAN_TRIALS, true);
        },
      },
    })(...args);
  }

  render() {
    return this.props.children;
  }
}

export default ExperimentsWrapper;



// WEBPACK FOOTER //
// ./src/experiments/ExperimentsWrapper.js